/**
 * Browser-agnostic helpers shared by SVG library picker UI and tests.
 *
 * Deployed builds route through asset-loader.getAssetURLSync(), resolving
 * content-hashed R2 SVG keys via the manifest. Localhost uses the on-disk
 * `external svgs/` cache so picker clicks can fetch raw SVG text without
 * R2 CORS getting in the way.
 */

import { getAssetURLSync } from '../core/asset-loader.js';

function encodeSvgFilename(svg) {
  const filename = String(svg?.filename || '').replace(/^\/+/, '');
  return filename
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
}

function shouldUseLocalSvgCache() {
  if (typeof window === 'undefined') return true;
  const location = window.location;
  if (!location) return false;
  const hostname = String(location.hostname || '').toLowerCase();
  return (
    location.protocol === 'file:' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '::1' ||
    hostname === '[::1]'
  );
}

export function getLocalSvgAssetUrl(svg) {
  const encoded = encodeSvgFilename(svg);
  return `./external%20svgs/${encoded}`;
}

export function getSvgAssetUrl(svg) {
  const encoded = encodeSvgFilename(svg);
  if (shouldUseLocalSvgCache()) return getLocalSvgAssetUrl(svg);
  return getAssetURLSync(`svgs/${encoded}`);
}

export function filterSvgLibraryEntries(
  svgs,
  { library: _library = 'icons', category = 'all', query = '' } = {}
) {
  const normalizedQuery = String(query || '')
    .trim()
    .toLowerCase();
  return (Array.isArray(svgs) ? svgs : []).filter((svg) => {
    // The category filter is no longer decorative-only. It was gated on
    // `library === 'decorative'` because only that mode had chips; the icon
    // library has them now (picker parity, 2026-09-02), and its entries carry
    // the same `category` field. `library` stays in the signature — it is part
    // of the caller's vocabulary and the next filter may well need it — and
    // `category: 'all'`, which every pre-existing icon-mode caller passes, is
    // still a no-op, so nothing that did not opt in has moved.
    if (category && category !== 'all' && svg.category !== category) return false;
    if (!normalizedQuery) return true;
    return (
      String(svg.name || '')
        .toLowerCase()
        .includes(normalizedQuery) ||
      String(svg.filename || '')
        .toLowerCase()
        .includes(normalizedQuery) ||
      String(svg.category || '')
        .toLowerCase()
        .includes(normalizedQuery) ||
      String(svg.sourceUrl || '')
        .toLowerCase()
        .includes(normalizedQuery) ||
      String(svg.sourceTemplate || '')
        .toLowerCase()
        .includes(normalizedQuery) ||
      String(svg.className || '')
        .toLowerCase()
        .includes(normalizedQuery)
    );
  });
}
